// crewbit-admin/src/hooks/useRemittanceLog.ts
//
// Hook contract: docs/references/MONEY_MODULE_SPEC_v1.md §Tax.7.1
// (RemittanceLogTable — admin variant of mobile's RemittanceLogCard).
// Read-only list of past tax_remittances for a single (businessId, taxType)
// tuple, sorted by period_end DESC. Direct supabase query (one-table read,
// no RPC envelope needed).
//
// Mirrors crewbit-mobile/hooks/useRemittanceLog.ts (Session 135 ship) with
// web-runtime divergence: Page Visibility API + window.addEventListener for
// MONEY_MUTATION_EVENT instead of useFocusEffect + DeviceEventEmitter.
//
// dataRef + inFlightRef + mountedRef discipline matches the rest of the
// admin hooks (useFinancialSummary, useTaxSummaryDetail).
//
// Pre-flight verified Cowork 2026-05-03: SELECT smoke against live DB
// confirms all 11 referenced columns exist on tax_remittances; 0 production
// rows today (substrate-fresh post-Session-134 migration 268).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { MONEY_MUTATION_EVENT } from './useFinancialSummary';
import type { TaxType } from '../lib/taxMutations';

const FOCUS_REFETCH_THROTTLE_MS = 30_000;

export interface RemittanceLogRow {
  id: string;
  tax_type: TaxType;
  period_type: string;
  period_start: string;
  period_end: string;
  status: 'open' | 'filed' | 'amended' | 'reopened';
  filed_at: string | null;
  confirmation_number: string | null;
  amount_owed: number;
  amount_collected: number;
  amount_itc: number;
}

export interface UseRemittanceLogArgs {
  /** Pass null/undefined to skip the fetch (returns initial state). */
  businessId: string | null;
  taxType: TaxType;
  /** Default 10. */
  limit?: number;
}

export interface UseRemittanceLogReturn {
  data: RemittanceLogRow[] | null;
  isLoading: boolean;
  isRefreshing: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const SELECT_COLS =
  'id, tax_type, period_type, period_start, period_end, status, filed_at, ' +
  'confirmation_number, amount_owed, amount_collected, amount_itc';

export function useRemittanceLog({
  businessId,
  taxType,
  limit = 10,
}: UseRemittanceLogArgs): UseRemittanceLogReturn {
  const [data, setData] = useState<RemittanceLogRow[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const dataRef = useRef<RemittanceLogRow[] | null>(null);
  useEffect(() => { dataRef.current = data; }, [data]);

  const lastFocusFetchRef = useRef<number>(0);
  const inFlightRef = useRef<boolean>(false);
  const mountedRef = useRef<boolean>(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const doFetch = useCallback(
    async (manual: boolean) => {
      if (!businessId) return;
      if (inFlightRef.current) return;
      inFlightRef.current = true;

      const isInitial = dataRef.current === null;
      if (isInitial) setIsLoading(true);
      else if (manual) setIsRefreshing(true);

      try {
        const { data: rows, error: queryError } = await supabase
          .from('tax_remittances')
          .select(SELECT_COLS)
          .eq('business_id', businessId)
          .eq('tax_type', taxType)
          .order('period_end', { ascending: false })
          .limit(limit);

        if (!mountedRef.current) return;
        if (queryError) {
          setError(new Error(queryError.message));
        } else {
          setData((rows ?? []) as unknown as RemittanceLogRow[]);
          setError(null);
        }
      } finally {
        if (mountedRef.current) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
        inFlightRef.current = false;
      }
    },
    [businessId, taxType, limit],
  );

  // Reset on tuple switch.
  useEffect(() => {
    setData(null);
    setError(null);
  }, [businessId, taxType]);

  // Mount fetch.
  useEffect(() => {
    if (!businessId) return;
    void doFetch(false);
  }, [businessId, taxType, doFetch]);

  // Page Visibility focus refetch (web-runtime equivalent of mobile's useFocusEffect).
  useEffect(() => {
    if (!businessId) return;
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastFocusFetchRef.current >= FOCUS_REFETCH_THROTTLE_MS) {
        lastFocusFetchRef.current = now;
        void doFetch(true);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => { document.removeEventListener('visibilitychange', onVisibility); };
  }, [businessId, doFetch]);

  // MONEY_MUTATION_EVENT cache invalidation.
  useEffect(() => {
    const onMutation = () => {
      if (businessId) void doFetch(true);
    };
    window.addEventListener(MONEY_MUTATION_EVENT, onMutation);
    return () => { window.removeEventListener(MONEY_MUTATION_EVENT, onMutation); };
  }, [businessId, doFetch]);

  const refetch = useCallback(async () => { await doFetch(true); }, [doFetch]);

  return useMemo(
    () => ({ data, isLoading, isRefreshing, error, refetch }),
    [data, isLoading, isRefreshing, error, refetch],
  );
}
